import React, { useState } from 'react';
import { Container, Card, CardBody, Table, Row, Col } from 'reactstrap';
import Select from 'react-select';
import {
  format,
  startOfDay,
  endOfDay,
  startOfMonth,
  endOfMonth,
  subDays,
  subMonths,
} from 'date-fns';
import dataService from 'services/dataService';
import { ADHERENCE_REPORT } from 'services/dataService/resources';
import common from 'constants/common';
import DownloadRoundedIcon from 'components/atoms/icons/downloadRoundedIcon';
import Pagination from 'components/molecules/paginationTransition';
import withLoader from 'components/organisms/withLoader';
import ActivityRow from 'components/templates/rpm/practice/adherenceReport/activityRow';
import { showResult, getUTCDates } from 'utils';
import useTable from 'utils/useTable';

const PER_PAGE = 100;
const DEFAULT_TIME_RANGE_KEY = 4;

const headings = [
  'Patient Name',
  'MRN',
  'DOB',
  'Insurance Plan Provider',
  'Insurance Plan Number',
  'Language',
  'Physician',
  'Phone Number',
  'Secondary Phone Number',
  'Email',
  'Prefer Contact Method',
  'Devices ID',
  'Devices Brand',
  'Adherence',
  'Last Reading',
];

const today = new Date();
const yesterday = subDays(today, 1);
const sevenDaysAgo = subDays(today, 7);
const fourteenDaysAgo = subDays(today, 14);

const dateRanges = [];
dateRanges.push({
  value: getUTCDates([startOfDay(today), endOfDay(today)]),
  label: 'Today',
});
dateRanges.push({
  value: getUTCDates([startOfDay(yesterday), endOfDay(yesterday)]),
  label: 'Yesterday',
});
dateRanges.push({
  value: getUTCDates([startOfDay(sevenDaysAgo), endOfDay(today)]),
  label: 'Last 7 Days',
});
dateRanges.push({
  value: getUTCDates([startOfDay(fourteenDaysAgo), endOfDay(today)]),
  label: 'Last 14 Days',
});
for (let i = 0; i < 12; i++) {
  const startTime = subMonths(today, i);
  const endTime = endOfMonth(startTime);
  const label = format(startTime, 'MMM-yyyy');
  dateRanges.push({
    value: getUTCDates([startOfMonth(startTime), endOfMonth(endTime)]),
    label,
  });
}

const AdherenceReport = () => {
  const [downloadLoading, setDownloadLoading] = useState(false);

  const {
    data: { data: adherence = [] },
    count,
    loading: reportLoading,
    page,
    setPage,
    filters,
    setFilters,
  } = useTable(ADHERENCE_REPORT, PER_PAGE, {
    startTime: dateRanges[DEFAULT_TIME_RANGE_KEY].value[0].getTime(),
    endTime: dateRanges[DEFAULT_TIME_RANGE_KEY].value[1].getTime(),
  });

  const sendReport = async () => {
    if (downloadLoading) {
      return;
    }
    setDownloadLoading(true);
    const { error } = await dataService.createOne(ADHERENCE_REPORT, filters);
    setDownloadLoading(false);
    return showResult(error, common.submitSuccessTypes.sendReport);
  };

  return (
    <Container>
      <div className="activity-report">
        <Card className="card-border">
          <CardBody className="card-outer">
            <div className="performance-review__header">
              <div className="activity-header">
                <h3>Report: Adherence</h3>
              </div>
              <div className="performance-review__header-icon">
                {withLoader(
                  downloadLoading,
                  <button onClick={sendReport}>
                    <DownloadRoundedIcon className="performance-review__header-icon-svg" />
                    <br />
                    <span>Download</span>
                  </button>,
                )}
              </div>
            </div>
            <div className="activity-body">
              <div className="sort-row">
                <Row>
                  <Col xl="2" lg="3" md="4" sm="6" xs="12">
                    <strong>Time Range</strong>
                    <Select
                      options={dateRanges}
                      defaultValue={dateRanges[DEFAULT_TIME_RANGE_KEY]}
                      classNamePrefix="select-type"
                      onChange={({ value: [startTime, endTime] }) =>
                        setFilters({
                          startTime: startTime.getTime(),
                          endTime: endTime.getTime(),
                        })
                      }
                    />
                  </Col>
                </Row>
              </div>
              <div className="table-sticky-header">
                {withLoader(
                  reportLoading,
                  <Table borderless cellSpacing="0" cellPadding="0">
                    <thead>
                      <tr>
                        {headings &&
                          headings.map((heading, i) => (
                            <th
                              className="table-status-column"
                              key={`table-heading-${i}`}
                            >
                              <div className="sort-div">{heading}</div>
                            </th>
                          ))}
                      </tr>
                    </thead>
                    <tbody>
                      {adherence.map((activityRow, i) => (
                        <ActivityRow
                          key={`table-row-${i}`}
                          activity={activityRow}
                        />
                      ))}
                    </tbody>
                  </Table>,
                )}
              </div>
              <div className="billing-table-button-pagination flex-row-reverse">
                <Pagination
                  page={page}
                  pageTotal={adherence.length}
                  itemsPerPage={PER_PAGE}
                  total={count}
                  handleFirstClick={() => setPage(1)}
                  handleLastClick={() => setPage(Math.ceil(count / PER_PAGE))}
                  handlePrevClick={() => setPage(page - 1)}
                  handleNextClick={() => setPage(page + 1)}
                  handleGoToPage={(pageNumber) => setPage(pageNumber)}
                />
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    </Container>
  );
};

export default AdherenceReport;
